import React, { useMemo, useState } from "react";
import * as Bananas from "bananas-commerce-admin";

import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

import { useSnackbar } from "notistack";

import {
  PublishingRuleCreateSchema,
  PublishingRuleRetrieveSchema,
} from "../../types";
import {
  daysType,
  formatDays,
  formatScheduleName,
  formatTime,
  GAP,
  GRID_FOUR_COLUMNS,
} from "../../utils";
import { statusColor } from "../AdStatusBadge";
import EditScheduleDialog from "../EditScheduleDialog";
import { useSchedulesCard } from "../SchedulesCard/context";

import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
export interface ScheduleCardProps {
  schedule: PublishingRuleRetrieveSchema;
  defaultExpanded?: boolean;
  refresh: () => void;
}

const ScheduleCard: React.FC<ScheduleCardProps> = ({
  defaultExpanded = false,
  schedule,
  refresh,
}) => {
  const { t } = Bananas.useI18n();
  const theme = useTheme();
  const api = Bananas.useApi();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [editScheduleDialogOpen, setEditScheduleDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { isStandalone } = useSchedulesCard();
  const { enqueueSnackbar } = useSnackbar();
  const status =
    new Date(schedule.from_date) <= new Date() &&
    new Date(schedule.to_date) >= new Date()
      ? "aktiv"
      : "inaktiv";
  const stopTime = useMemo(() => {
    return formatTime(schedule.hour, schedule.minute);
  }, [schedule.hour, schedule.minute]);

  const handleEditSchedule = async (data: PublishingRuleCreateSchema) => {
    const response = await api.operations[
      "mediahouse.publishing-rule:edit"
    ]?.call({ body: data, params: { group_id: schedule.group_id } });

    if (response?.status === 200) {
      enqueueSnackbar("Utgivningsschemat har uppdaterats");
    }

    refresh();
  };

  const handleDeleteSchedule = async () => {
    const response = await api.operations["mediahouse.times:delete"]?.call({
      params: { group_id: schedule.group_id },
    });

    if (response?.status === 200) {
      enqueueSnackbar("Utgivningsschemat har tagits bort");
    }

    refresh();
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    setIsDeleteDialogOpen(false);
    handleDeleteSchedule();
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
  };
  return (
    <>
      <Card
        sx={{
          borderRadius: !isStandalone ? 2 : 0,
          border: !isStandalone ? null : "none",
        }}
        variant="outlined"
      >
        <CardContent sx={{ p: 0, pb: "0 !important" }}>
          <Stack
            alignItems="center"
            borderBottom={1}
            borderColor="divider"
            direction="row"
            justifyContent="space-between"
            p={2}
          >
            <Stack alignItems="center" direction="row" gap={2}>
              <Chip
                label={status}
                size="small"
                sx={{
                  textTransform: "uppercase",
                  bgcolor:
                    status === "aktiv"
                      ? statusColor["customer_approved"][0]
                      : statusColor["archived"][0],
                  color:
                    status === "aktiv"
                      ? statusColor["customer_approved"][1]
                      : statusColor["archived"][1],
                  height: theme.spacing(3),
                  fontSize: theme.typography.pxToRem(12),
                  width: 80,
                }}
              />

              <Typography
                fontWeight={500}
                sx={{ paddingRight: 2 }}
                variant="body1"
              >
                {schedule.from_date} - {schedule.to_date}
              </Typography>
              <Typography fontWeight={500} variant="body1">
                {formatScheduleName(schedule)}
              </Typography>
            </Stack>
            <IconButton
              size="small"
              sx={{
                transform: expanded ? "none" : "rotate(180deg)",
                transition: theme.transitions.create("transform"),
              }}
              onClick={() => setExpanded(!expanded)}
            >
              <KeyboardArrowUpIcon />
            </IconButton>
          </Stack>

          <Collapse in={expanded}>
            <Box>
              <Box
                sx={{
                  bgcolor:
                    theme.palette.mode === "dark" ? "grey.900" : "grey.100",
                  display: "grid",
                  py: GAP,
                  px: GAP * 1.25,
                  gap: GAP,
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: GRID_FOUR_COLUMNS,
                    gap: GAP,
                  }}
                >
                  <Box
                    gridTemplateColumns="max-content 3fr"
                    sx={{ gridColumn: "span 1", display: "grid", gap: GAP / 2 }}
                  >
                    <Typography
                      color="text.secondary"
                      sx={{ gridColumn: "span 1", pr: 1 }}
                      variant="body2"
                    >
                      {t("Från")}
                    </Typography>
                    <Typography sx={{ gridColumn: "span 1" }} variant="body2">
                      {/* TODO Parse date and show in correct format */}
                      {schedule.from_date}
                    </Typography>

                    <Typography
                      color="text.secondary"
                      sx={{ gridColumn: "span 1", pr: 1 }}
                      variant="body2"
                    >
                      {t("Till")}
                    </Typography>
                    <Typography sx={{ gridColumn: "span 1" }} variant="body2">
                      {/* TODO Parse date and show in correct format */}
                      {schedule.to_date}
                    </Typography>
                  </Box>
                  <Stack
                    direction="row"
                    gap={GAP}
                    sx={{ gridColumn: "span 3" }}
                  >
                    <Typography color="text.secondary" variant="body2">
                      {t("Gäller")}
                    </Typography>
                    <Typography variant="body2">
                      {formatDays(schedule)}
                    </Typography>
                  </Stack>
                </Box>

                <Divider />

                <Box>
                  <Typography
                    gutterBottom
                    color="text.secondary"
                    variant="subtitle2"
                  >
                    {t("Exkluderade utgivningsdagar")}
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: GRID_FOUR_COLUMNS,
                      gap: GAP,
                    }}
                  >
                    <Box
                      display="grid"
                      gap={GAP / 4}
                      gridColumn="span 2"
                      gridTemplateColumns="subgrid"
                    >
                      {schedule.days_exceptions.map((date) => (
                        <Box
                          key={date.date}
                          display="grid"
                          gap={GAP}
                          gridColumn="span 2"
                          gridTemplateColumns="subgrid"
                        >
                          <Typography
                            sx={{ gridColumn: "span 1" }}
                            variant="body2"
                          >
                            {date.day_name}
                          </Typography>
                          <Typography
                            sx={{ gridColumn: "span 1" }}
                            variant="body2"
                          >
                            {new Date(date.date).toISOString().split("T")[0]}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
                <Divider />
                <Box>
                  <Typography
                    gutterBottom
                    color="text.secondary"
                    variant="subtitle2"
                  >
                    {t("Stopptider")}
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: GRID_FOUR_COLUMNS,
                      gap: GAP,
                    }}
                  >
                    <Stack direction="row" gap={GAP} gridColumn="span 1">
                      <Typography variant="body2">{t("Senast")}</Typography>
                      <Typography variant="body2">{stopTime}</Typography>
                    </Stack>
                    <Stack direction="row" gap={GAP} gridColumn="span 3">
                      <Typography variant="body2">{t("Gäller")}</Typography>
                      <Typography sx={{ gridColumn: "span 2" }} variant="body2">
                        {schedule.publication_lead_time +
                          " " +
                          daysType[schedule.days_type]}
                      </Typography>
                    </Stack>
                  </Box>
                </Box>
                <Divider />
                <Box>
                  <Typography
                    gutterBottom
                    color="text.secondary"
                    variant="subtitle2"
                  >
                    {t("Anpassade stopptider")}
                  </Typography>
                  <Box
                    display="grid"
                    gap={GAP / 4}
                    gridTemplateColumns={GRID_FOUR_COLUMNS}
                  >
                    {schedule.times_exceptions.length > 0 ? (
                      <>
                        <Box
                          display="grid"
                          gridColumn="span 4"
                          gridTemplateColumns="subgrid"
                        >
                          <Typography
                            color="text.secondary"
                            gridColumn="span 1"
                            variant="subtitle2"
                          >
                            {t("Gäller")}
                          </Typography>
                          <Typography
                            color="text.secondary"
                            gridColumn="span 1"
                            variant="subtitle2"
                          >
                            {t("Stoppdag")}
                          </Typography>
                          <Typography
                            color="text.secondary"
                            gridColumn="span 1"
                            variant="subtitle2"
                          >
                            {t("Klockslag")}
                          </Typography>
                        </Box>
                        {schedule.times_exceptions.map((exception) => (
                          <Box
                            key={exception.for_date + exception.deadline_date}
                            display="grid"
                            gridColumn="span 4"
                            gridTemplateColumns="subgrid"
                          >
                            <Typography variant="body2">
                              {/* TODO Parse date and show in correct format */}
                              {exception.for_date}
                            </Typography>
                            <Typography variant="body2">
                              {/* TODO Parse date and show in correct format */}
                              {exception.deadline_date}
                            </Typography>
                            <Typography variant="body2">
                              {formatTime(exception.hour, exception.minute)}
                            </Typography>
                          </Box>
                        ))}
                      </>
                    ) : (
                      <Typography gridColumn="span 4" variant="body2">
                        {t(
                          "Det finns inga anpassade stopptider för det här utgivningsschemat.",
                        )}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Divider />
                <Box>
                  <Typography
                    gutterBottom
                    color="text.secondary"
                    variant="subtitle2"
                  >
                    {t("Titlar")}
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: GRID_FOUR_COLUMNS,
                      gap: GAP / 4,
                    }}
                  >
                    {schedule.publications.map((publication) => (
                      <Typography
                        key={publication.abbreviation}
                        sx={{
                          textDecoration: "underline",
                          textDecorationColor: "divider",
                        }}
                        variant="body2"
                      >
                        {publication.name}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box
              bgcolor={theme.palette.mode === "dark" ? "grey.900" : "white"}
              borderTop={`1px solid ${theme.palette.divider}`}
              p={GAP}
            >
              <Stack direction="row" justifyContent="flex-end" spacing={1}>
                <Button variant="text" onClick={handleDeleteClick}>
                  {t("Ta bort")}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setEditScheduleDialogOpen(true)}
                >
                  {t("Ändra")}
                </Button>
              </Stack>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
      <EditScheduleDialog
        initialSchedule={{
          ...schedule,
          publications: schedule.publications.map(
            (publication) => publication.abbreviation,
          ),
        }}
        open={editScheduleDialogOpen}
        onClose={() => setEditScheduleDialogOpen(false)}
        onSubmit={handleEditSchedule}
      />
      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
};

export default ScheduleCard;
